// LeadsView.jsx — the Leads page (Braeden 8/10).
//
// Three tiers, top to bottom:
//   1. ONE big box across the whole top — the lead total.
//   2. The week's numbers — conversions, cost per lead, CPM, everything they
//      actually want to know. Same card shape, plain grey.
//   3. Every social platform the leads come from, in that platform's BRAND
//      colour, each linking out to that platform.
//
// Card shape throughout matches the revenue bubbles on the company dashboard:
// wide and short, small label top-left, big value under it, 14px radius.
//
// Honesty rule kept from the first pass: a platform that isn't connected shows
// no number, and a metric with no data shows "—". Never an invented figure.
// Real counts come from services/leadSpine (website + referral today).
//
// See ARIA_COMPANY_TABS_SPEC.md.

import React from 'react';

// Real brand marks — each platform's actual logo and its actual colour, not a
// letter in a coloured square (owner 8/10: "like the acual facebook brand ...
// make it look REAL"). Google keeps its white-card treatment because the
// four-colour G is only correct on white; the rest carry their brand fill.
const LOGO = {
  facebook: (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07Z"/></svg>
  ),
  instagram: (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 2.16c3.2 0 3.58.01 4.85.07 3.25.15 4.77 1.69 4.92 4.92.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.15 3.23-1.66 4.77-4.92 4.92-1.27.06-1.64.07-4.85.07s-3.58-.01-4.85-.07c-3.26-.15-4.77-1.7-4.92-4.92C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.15-3.23 1.66-4.77 4.92-4.92C8.42 2.17 8.8 2.16 12 2.16ZM12 0C8.74 0 8.33.01 7.05.07 2.7.27.28 2.69.08 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.2 4.36 2.62 6.78 6.98 6.98C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c4.35-.2 6.78-2.62 6.98-6.98.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.2-4.35-2.62-6.78-6.98-6.98C15.67.01 15.26 0 12 0Zm0 5.84a6.16 6.16 0 1 0 0 12.32 6.16 6.16 0 0 0 0-12.32ZM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8Zm6.41-11.85a1.44 1.44 0 1 0 0 2.88 1.44 1.44 0 0 0 0-2.88Z"/></svg>
  ),
  google: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82Z"/>
      <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24Z"/>
      <path fill="#FBBC05" d="M5.27 14.29a7.2 7.2 0 0 1 0-4.58V6.62H1.29a12 12 0 0 0 0 10.76l3.98-3.09Z"/>
      <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75Z"/>
    </svg>
  ),
  yelp: (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M10.6 13.2 5.4 15.6c-.7.3-1.4-.2-1.3-1 .1-.5.3-1.2.6-1.9.3-.7.6-1.3.9-1.7.4-.6 1.3-.5 1.7.1l3.3 4.5c.4.6-.3 1.3-1 1.6Zm1.6 1.9 1 5.6c.1.8-.7 1.3-1.4.9-.5-.3-1-.7-1.5-1.2-.5-.5-.9-1-1.1-1.4-.4-.6.1-1.4.8-1.4l5.6-.1c.7 0 1 .9.6 1.6Zm-1.9-6.9L9 2.6c-.2-.8.5-1.4 1.2-1.2 1 .3 2.5.9 3.2 1.4.6.4.6 1.3 0 1.7l-4.6 3.4c-.6.4-1.4-.1-1.5-.9Zm3.5 3.3 5.5-1.6c.8-.2 1.4.5 1.2 1.2-.2.6-.5 1.3-.9 1.9-.4.6-.8 1.1-1.2 1.4-.5.4-1.3.1-1.5-.6l-1.6-5.3c-.2-.7.6-1.2 1.3-1Zm.6 4.1 4.7 3.1c.7.4.6 1.4-.1 1.7-.5.2-1.2.4-1.9.5-.7.1-1.4.1-1.9 0-.7-.1-1-.9-.6-1.5l3.1-4.7c.4-.6 1.3-.3 1.6.4Z"/></svg>
  ),
  nextdoor: (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 0 1.5 6v12L12 24l10.5-6V6L12 0Zm4.6 16.6h-2.9v-4.4c0-1.1-.6-1.8-1.6-1.8-1 0-1.7.7-1.7 1.9v4.3H7.5V8.1h2.8v1.1c.6-.8 1.6-1.3 2.8-1.3 2.1 0 3.5 1.4 3.5 3.7v5Z"/></svg>
  ),
  website: (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 0a12 12 0 1 0 0 24 12 12 0 0 0 0-24Zm7.9 7h-3.3a17 17 0 0 0-1.5-3.9A9.6 9.6 0 0 1 19.9 7ZM12 2.2c.9 1.2 1.6 2.7 2.1 4.8H9.9c.5-2.1 1.2-3.6 2.1-4.8ZM2.4 14a9.5 9.5 0 0 1 0-4h3.8a20 20 0 0 0 0 4H2.4Zm.9 2.5h3.3c.4 1.5.9 2.8 1.5 3.9a9.6 9.6 0 0 1-4.8-3.9Zm3.3-9H3.3a9.6 9.6 0 0 1 4.8-3.9A17 17 0 0 0 6.6 7.5ZM12 21.8c-.9-1.2-1.6-2.7-2.1-4.8h4.2c-.5 2.1-1.2 3.6-2.1 4.8Zm2.5-7.3h-5a17.4 17.4 0 0 1 0-4h5a17.4 17.4 0 0 1 0 4Zm.4 6.4c.6-1.1 1.1-2.4 1.5-3.9h3.3a9.6 9.6 0 0 1-4.8 3.9Zm2-6.4a20 20 0 0 0 0-4h3.8a9.5 9.5 0 0 1 0 4h-3.8Z"/></svg>
  ),
  referral: (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M18 16.1c-.8 0-1.5.3-2 .8l-7.1-4.2c.1-.2.1-.5.1-.7s0-.5-.1-.7l7-4.1c.6.5 1.3.8 2.1.8a3.1 3.1 0 1 0-3.1-3.1c0 .3 0 .5.1.7L8 9.7c-.6-.5-1.3-.8-2.1-.8a3.1 3.1 0 0 0 0 6.2c.8 0 1.5-.3 2.1-.8l7.1 4.2c-.1.2-.1.4-.1.6a3 3 0 1 0 3-3Z"/></svg>
  ),
  word: (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M20 2H4a2 2 0 0 0-2 2v18l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2ZM7 9h10v2H7V9Zm7 5H7v-2h7v2Zm3-6H7V6h10v2Z"/></svg>
  ),
};

// Braeden 8/11: "really the top 4 are going to be facebook, insta, google,
// website" — so those lead the declared order, and everything else falls in
// behind them. Once real counts exist the list re-sorts by performance, but a
// brand-new account with all zeros still opens on the four that matter.
const PLATFORMS = [
  // Links go to the place campaigns are actually RUN and TRACKED, not the
  // generic business landing page (Braeden 8/11). Facebook and Instagram share
  // one destination because Meta runs both from the same Ads Manager.
  { key: 'facebook',  label: 'Facebook',      color: '#1877F2',
    url: 'https://adsmanager.facebook.com/adsmanager', manage: 'Meta Ads Manager' },
  { key: 'instagram', label: 'Instagram',     color: '#C13584',
    url: 'https://adsmanager.facebook.com/adsmanager', manage: 'Meta Ads Manager',
    setup: 'Instagram ads run through Meta Ads Manager, same as Facebook',
    bg: 'radial-gradient(circle at 28% 108%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)' },
  { key: 'google',    label: 'Google',        color: '#ffffff', ink: '#1f1f1f',
    url: 'https://ads.google.com/aw/campaigns', manage: 'Google Ads' },
  { key: 'website',   label: 'Website',       color: '#111827', url: null, manage: 'your site',
    setup: 'Already on — every form and quote lands here' },
  { key: 'yelp',      label: 'Yelp',          color: '#FF1A1A',
    url: 'https://biz.yelp.com/advertise', manage: 'Yelp Ads' },
  { key: 'nextdoor',  label: 'Nextdoor',      color: '#8ED500', ink: '#0b1a00',
    url: 'https://ads.nextdoor.com/', manage: 'Nextdoor Ads' },
  { key: 'referral',  label: 'Referrals',     color: '#16a34a', url: null, manage: 'referrals',
    setup: 'Give out a referral link and Aria tags who came from it' },
  // There is nothing to "sign in to" for word of mouth — the copy said
  // "Sign in to word of mouth", which is nonsense. It is a thing Aria records
  // when a caller says how they heard about you.
  { key: 'word',      label: 'Word of mouth', color: '#4b5563', url: null, manage: 'word of mouth',
    setup: 'Aria asks callers how they heard about you and logs the answer' },
];

// Inside the Electron build the Assist-mode nudge is pointless — he is already
// in the app. userAgent is the one signal available to this component.
const IS_DESKTOP_APP = typeof navigator !== 'undefined' && /Electron/i.test(navigator.userAgent || '');

// Only these are actually recorded today (leadSpine /l/:token links).
const LIVE_SOURCES = new Set(['website', 'referral']);

const PERIODS = [
  { key: 'week',  label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'year',  label: 'Year' },
];

export default function LeadsView({ onNavigate }) {
  // Braeden 8/11: the page opens with ONLY what he has actually added — a new
  // account is just Website. Everything else lives behind "Add more", so the
  // list is a record of his real setup instead of a wall of things he hasn't
  // signed up for.
  // "Add more" opens a picker (Braeden 8/11) rather than expanding the list in
  // place: choosing a channel is a separate task from reading your numbers, and
  // setting one up is really just logging in to it. The search box filters the
  // known list, and anything it doesn't recognise gets handed to Aria to find.
  const [addOpen, setAddOpen] = React.useState(false);
  const [addQuery, setAddQuery] = React.useState('');
  // channels this owner has actually added — persisted server-side, so pressing
  // "Set up" moves the channel into his list and it is still there tomorrow
  const [connected, setConnected] = React.useState(() => new Set(['website']));

  React.useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const r = await fetch('/api/leads/connections');
        const j = await r.json();
        if (!dead && j && Array.isArray(j.connections)) {
          setConnected(new Set(j.connections.map((c) => c.key)));
        }
      } catch (_) { /* keep the website-only default */ }
    })();
    return () => { dead = true; };
  }, []);

  const addChannel = React.useCallback(async (key) => {
    setConnected((s) => new Set([...s, key]));      // optimistic: the row appears at once
    try {
      await fetch('/api/leads/connections', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ channel: key, action: 'add' }),
      });
    } catch (_) { /* the row still shows; it re-syncs on next load */ }
  }, []);
  const [period, setPeriod] = React.useState('week');
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  // HISTORY (Braeden 8/11): a button top-right that opens a picker for a
  // specific month or year. Deliberately NO week option — "last week" is a
  // relative window, not a thing you navigate back to, and a list of 52 of them
  // is unusable. `histSel` null = the live current period.
  const [histOpen, setHistOpen] = React.useState(false);
  const [histSel, setHistSel] = React.useState(null);   // {kind:'month'|'year', key, label}

  const MONTHS_BACK = 12, YEARS_BACK = 4;
  const HISTORY = React.useMemo(() => {
    const now = new Date();
    const months = [];
    for (let i = 0; i < MONTHS_BACK; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        kind: 'month',
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
        label: d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
      });
    }
    const years = [];
    for (let i = 0; i < YEARS_BACK; i++) {
      const y = now.getFullYear() - i;
      years.push({ kind: 'year', key: String(y), label: String(y) });
    }
    return { months, years };
  }, []);

  // Escape closes the add-a-channel sheet (outside click is handled on the overlay)
  React.useEffect(() => {
    if (!addOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setAddOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [addOpen]);

  // close on outside click / Escape, like any other menu
  const histRef = React.useRef(null);
  React.useEffect(() => {
    if (!histOpen) return undefined;
    const onDoc = (e) => { if (histRef.current && !histRef.current.contains(e.target)) setHistOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setHistOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [histOpen]);

  React.useEffect(() => {
    let dead = false;
    setLoading(true);
    (async () => {
      try {
        const ctl = new AbortController();
        const t = setTimeout(() => ctl.abort(), 8000);
        // a history pick overrides the live period and asks for that exact span
        const q = histSel
          ? `${histSel.kind}=${encodeURIComponent(histSel.key)}`
          : `period=${encodeURIComponent(period)}`;
        const r = await fetch(`/api/leads/summary?${q}`, { signal: ctl.signal });
        clearTimeout(t);
        if (!r.ok) throw new Error(String(r.status));
        const j = await r.json();
        if (!dead) setData(j || null);
      } catch (_) {
        if (!dead) setData(null); // endpoint not built yet — expected
      } finally {
        if (!dead) setLoading(false);
      }
    })();
    return () => { dead = true; };
  }, [period, histSel]);

  const counts = (data && (data.bySource || data.counts)) || {};
  // "has a number" now means the owner ADDED it, not a hardcoded list
  const countFor = (k) => (connected.has(k) ? Number(counts[k] || 0) : null);
  const total = PLATFORMS.reduce((n, p) => n + (countFor(p.key) || 0), 0);

  // The week's numbers. Anything the backend doesn't send reads "—".
  const m = (data && data.metrics) || {};
  const num = (v, suffix = '') => (v === undefined || v === null || v === '' ? '—' : `${v}${suffix}`);
  const money = (v) => (v === undefined || v === null || v === '' ? '—' : `$${Number(v).toLocaleString()}`);

  const METRICS = [
    { label: 'Conversions',    value: num(m.conversions) },
    { label: 'Conversion rate', value: m.conversionRate != null ? `${m.conversionRate}%` : (total ? `${Math.round(((m.conversions || 0) / total) * 100)}%` : '—') },
    { label: 'Cost per lead',  value: money(m.costPerLead) },
    { label: 'CPM',            value: money(m.cpm) },
    { label: 'Ad spend',       value: money(m.spend) },
    { label: 'Avg response',   value: num(m.avgResponseMins, ' min') },
    { label: 'Booked from leads', value: money(m.bookedValue) },
    { label: 'Answered',       value: m.answeredPct != null ? `${m.answeredPct}%` : '—' },
  ];

  const periodWord = PERIODS.find((p) => p.key === period)?.label.toLowerCase();

  return (
    <div className="leadsv">
      <style>{CSS}</style>

      <div className="lv-toprow">
        <div className="lv-periods">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              type="button"
              className={`lv-period${!histSel && period === p.key ? ' is-on' : ''}`}
              onClick={() => { setHistSel(null); setPeriod(p.key); }}
            >{p.label}</button>
          ))}
        </div>

        <div className="lv-hist" ref={histRef}>
          <button
            type="button"
            className={`lv-histbtn${histSel ? ' is-on' : ''}`}
            onClick={() => setHistOpen((v) => !v)}
          >
            {histSel ? histSel.label : 'History'} &#9662;
          </button>
          {histOpen && (
            <div className="lv-histmenu">
              {histSel && (
                <button type="button" className="lv-histitem is-clear"
                  onClick={() => { setHistSel(null); setHistOpen(false); }}
                >Back to current</button>
              )}
              <div className="lv-histgroup">Month</div>
              {HISTORY.months.map((h) => (
                <button key={h.key} type="button"
                  className={`lv-histitem${histSel && histSel.key === h.key ? ' is-sel' : ''}`}
                  onClick={() => { setHistSel(h); setHistOpen(false); }}
                >{h.label}</button>
              ))}
              <div className="lv-histgroup">Year</div>
              {HISTORY.years.map((h) => (
                <button key={h.key} type="button"
                  className={`lv-histitem${histSel && histSel.key === h.key ? ' is-sel' : ''}`}
                  onClick={() => { setHistSel(h); setHistOpen(false); }}
                >{h.label}</button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 1 — one big box across the whole top */}
      <div className="lv-hero">
        <div className="lv-hero__label">
          {histSel ? `Leads · ${histSel.label}` : `Leads · this ${periodWord}`}
        </div>
        <div className="lv-hero__value">{loading ? '—' : total.toLocaleString()}</div>
      </div>

      {/* 2 — WHERE THEY COME FROM, moved above the metrics (Braeden 8/11).
          Eight gold tiles all reading "—" were taking the whole screen and
          pushing the ranked list below the fold, so the one thing worth reading
          was the one thing you had to scroll for. Ranking first, numbers after. */}
      <div className="lv-sectionhead">Where they come from</div>
      {/* A RANKED LIST, not a grid of equal tiles (Braeden 8/11). A grid says
          every channel matters the same; a list sorted by leads says which one
          is actually feeding him — which is the only reason to look at this. */}
      <div className="lv-list">
        {(() => {
          const all = PLATFORMS.map((p, idx) => ({ p, idx, n: countFor(p.key) }));
          // ADDED = a channel that actually reports (LIVE_SOURCES). Those are
          // the only ones on the page by default; a fresh account is just
          // Website. Everything else is a thing he COULD add, and lives behind
          // the button so the list stays a record of his real setup.
          const added = all.filter((r) => r.n !== null);
          const available = all.filter((r) => r.n === null);
          // Best performer first; ties fall back to declared order, which puts
          // Website at the top of a brand-new account.
          const score = (r) => (r.n && r.n > 0 ? r.n : -1);
          added.sort((a, b) => (score(b) - score(a)) || (a.idx - b.idx));
          const rows = added;   // the page shows ONLY what he has; the rest live in the picker
          // Every row looks the same — no greying, no bar. Count on the left of
          // the button, management link on the right.
          return rows.map(({ p, n }) => (
            <div
              key={p.key}
              className="lv-row"
              // --brand paints the left edge. Google's card colour is white and
              // would vanish against --surface, so it borrows its blue.
              style={{ '--brand': p.key === 'google' ? '#4285F4' : p.color }}
            >
              <span className="lv-chip" style={{ background: p.bg || p.color, color: p.ink || '#fff' }}>
                <span className="lv-logo">{LOGO[p.key]}</span>
              </span>
              <span className="lv-row__name">
                {p.label}
                <small>{p.manage}</small>
              </span>
              <span className="lv-row__n">
                {n === null ? '' : (loading ? '—' : n.toLocaleString())}
                {n !== null && <small>leads</small>}
              </span>
              {p.url ? (
                <a className="lv-row__go" href={p.url} target="_blank" rel="noopener noreferrer">
                  {n === null ? 'Add' : 'Link'} &rarr;
                </a>
              ) : (
                <button
                  type="button"
                  className="lv-row__go as-btn"
                  onClick={(e) => { e.preventDefault(); onNavigate && onNavigate('integrations'); }}
                >{n === null ? 'Add' : 'Link'} &rarr;</button>
              )}
            </div>
          ));
        })()}
      </div>

      {/* The rest of the channels are one click away, not sitting on the page
          as eight things he hasn't signed up for. */}
      <button
        type="button"
        className="lv-addmore"
        onClick={() => { setAddQuery(''); setAddOpen(true); }}
      >+&nbsp;&nbsp;Add more</button>

      {addOpen && (() => {
        const taken = connected;   // hide what he already has
        const q = addQuery.trim().toLowerCase();
        const list = PLATFORMS
          .filter((p) => !taken.has(p.key))
          .filter((p) => !q || p.label.toLowerCase().includes(q) || (p.manage || '').toLowerCase().includes(q));
        return (
          <div className="lv-modal" onMouseDown={() => setAddOpen(false)}>
            <div className="lv-sheet" onMouseDown={(e) => e.stopPropagation()}>
              <div className="lv-sheet__top">
                <div>
                  <h3>Add a channel</h3>
                  <p>Pick where your leads come from. Setting one up is just signing in to it.</p>
                </div>
                <button type="button" className="lv-x" onClick={() => setAddOpen(false)}>&times;</button>
              </div>

              <input
                className="lv-search"
                autoFocus
                value={addQuery}
                onChange={(e) => setAddQuery(e.target.value)}
                placeholder="Search, or type anything and Aria will find it…"
              />

              <div className="lv-choices">
                {list.map((p) => (
                  <a
                    key={p.key}
                    className="lv-choice"
                    style={{ '--brand': p.key === 'google' ? '#4285F4' : p.color }}
                    // record it FIRST, then send him to sign in — so the channel
                    // is on his page when he comes back from that tab
                    onClick={(e) => {
                      addChannel(p.key);
                      setAddOpen(false);
                      if (!p.url) { e.preventDefault(); onNavigate && onNavigate('integrations'); }
                    }}
                    {...(p.url
                      ? { href: p.url, target: '_blank', rel: 'noopener noreferrer' }
                      : { href: '#' })}
                  >
                    <span className="lv-chip" style={{ background: p.bg || p.color, color: p.ink || '#fff' }}>
                      <span className="lv-logo">{LOGO[p.key]}</span>
                    </span>
                    <span className="lv-choice__name">{p.label}
                      <small>{p.setup || `Sign in to ${p.manage}`}</small>
                    </span>
                    <span className="lv-choice__go">Set up &rarr;</span>
                  </a>
                ))}

                {/* Nothing matched — hand it to Aria instead of a dead end. */}
                {q && list.length === 0 && (
                  <button
                    type="button"
                    className="lv-choice lv-choice--ask"
                    onClick={() => { setAddOpen(false); onNavigate && onNavigate('ask'); }}
                  >
                    <span className="lv-chip lv-chip--ask">?</span>
                    <span className="lv-choice__name">
                      Ask Aria to find “{addQuery.trim()}”
                      <small>She’ll look it up and walk you through connecting it</small>
                    </span>
                    <span className="lv-choice__go">Ask &rarr;</span>
                  </button>
                )}
              </div>

              {/* Linking an ad account means signing in and clicking through a
                  console. In the desktop app Aria can drive that with Assist
                  mode; in a browser tab she can only tell him where to click.
                  So when he is NOT in the app, she says so and offers it. */}
              {!IS_DESKTOP_APP && (
                <a className="lv-appnudge" href="/aria-downloads.html" target="_blank" rel="noopener noreferrer">
                  <span className="lv-appnudge__ic">A</span>
                  <span className="lv-appnudge__tx">
                    <b>This is easier in the Windows app.</b>
                    I can open the sign-in with you and fill it out — you just approve it.
                  </span>
                  <span className="lv-appnudge__go">Download &rarr;</span>
                </a>
              )}
            </div>
          </div>
        );
      })()}

      {/* 3 — the numbers, AFTER the ranking. When none of them are measured yet
          they collapse into one quiet line instead of eight gold tiles shouting
          "—" across the top of the screen. */}
      {(() => {
        const measured = METRICS.filter((x) => x.value !== '—' && x.value != null);
        if (!loading && measured.length === 0) {
          return (
            <div className="lv-nometrics">
              Conversions, cost per lead, ad spend and response time appear here once a
              channel is connected and the first leads land.
            </div>
          );
        }
        return (
          <div className="lv-metrics">
            {(loading ? METRICS : measured).map((x) => (
              <div className="lv-metric" key={x.label}>
                <div className="lv-metric__label">{x.label}</div>
                <div className="lv-metric__value">{loading ? '—' : x.value}</div>
              </div>
            ))}
          </div>
        );
      })()}
    </div>
  );
}

const CSS = `
.leadsv { padding:22px clamp(14px,3vw,34px) 60px; max-width:1180px; margin:0 auto; }

/* period pills left, History right */
.lv-toprow { display:flex; align-items:center; justify-content:space-between; gap:12px;
  margin-bottom:14px; }
.lv-hist { position:relative; }
.lv-histbtn { font:inherit; font-size:14px; font-weight:700; cursor:pointer;
  padding:10px 16px; border-radius:11px; color:var(--text);
  background:var(--surface); border:1px solid var(--border);
  transition:border-color .18s, background .18s; }
.lv-histbtn:hover { border-color:var(--text-muted); }
.lv-histbtn.is-on { border-color:var(--accent); color:var(--accent); }
.lv-histmenu { position:absolute; right:0; top:calc(100% + 8px); z-index:40;
  min-width:230px; max-height:min(420px,60vh); overflow-y:auto;
  background:var(--surface); border:1px solid var(--border); border-radius:14px;
  box-shadow:0 18px 44px rgba(0,0,0,.34); padding:8px; }
.lv-histgroup { font-size:10.5px; font-weight:800; letter-spacing:.12em; text-transform:uppercase;
  color:var(--text-muted); padding:12px 12px 6px; }
.lv-histgroup:first-child { padding-top:6px; }
.lv-histitem { display:block; width:100%; text-align:left; font:inherit; font-size:14px;
  font-weight:600; cursor:pointer; padding:10px 12px; border-radius:9px;
  color:var(--text); background:transparent; border:0; }
.lv-histitem:hover { background:var(--border); }
.lv-histitem.is-sel { color:var(--accent); }
.lv-histitem.is-clear { color:var(--text-muted); font-weight:700;
  border-bottom:1px solid var(--border); border-radius:9px 9px 0 0; margin-bottom:4px; }

.lv-periods { display:flex; gap:6px; background:rgba(255,255,255,.10); padding:4px;
  border-radius:999px; width:fit-content; margin:0 0 12px; }
.lv-period { border:0; background:none; padding:7px 15px; border-radius:999px; cursor:pointer;
  font-weight:700; font-size:14px; color:#9aa4b6; }
.lv-period.is-on { background:#fff; color:#0b1020; box-shadow:0 1px 3px rgba(16,32,64,.14); }

/* 1 — the big one, full width. Owner 8/10: not grey — gold. It matches the
   HowTo wordmark, and his own "Unpaid / overdue" card already proves dark ink
   on yellow reads well in this design. */
/* Plain surface, not gold (Braeden 8/11: "needs to be a normal color not
   yellow"). A 200px block of brand gold shouting "0" was the loudest thing on
   the page and said nothing; it reads as a stat card now and flips with the
   Night/Day switch like every other panel. */
.lv-hero { border-radius:18px; padding:22px 26px 24px; color:var(--text);
  background:var(--surface); border:1px solid var(--border);
  box-shadow:0 2px 10px rgba(0,0,0,.16); }
.lv-hero__label { font-weight:800; font-size:12px; letter-spacing:.1em;
  text-transform:uppercase; color:var(--text-muted); }
.lv-hero__value { font-weight:800; letter-spacing:-.04em; line-height:1;
  font-size:clamp(44px,6vw,68px); margin-top:10px; }

.lv-metrics { margin-top:12px; display:grid; gap:12px;
  grid-template-columns:repeat(auto-fill, minmax(190px,1fr)); }
.lv-metric { border-radius:14px; padding:16px 18px 18px; color:var(--text);
  background:var(--surface); border:1px solid var(--border); }
.lv-metric__label { font-weight:800; font-size:11px; letter-spacing:.1em;
  text-transform:uppercase; color:var(--text-muted); }
.lv-metric__value { font-weight:800; letter-spacing:-.03em; font-size:clamp(26px,3vw,34px);
  line-height:1; margin-top:9px; }

.lv-sectionhead { margin:22px 0 10px; font-weight:800; letter-spacing:-.02em;
  font-size:19px; color:var(--text); }

/* 3 — platforms, brand colours, links out */
.lv-grid { display:grid; gap:12px; grid-template-columns:repeat(auto-fill, minmax(230px,1fr)); }

/* ── ranked source list ─────────────────────────────────────────────────
   One row per channel, ordered by leads. The whole row is the link to that
   channel's own management dashboard, so "click into it" needs no button. */
/* Rows are DELIBERATELY tall (Braeden 8/11: "needs to be longer, each fucking
   one"). At 61px they read as a settings menu; at ~104px each channel is its
   own object on the page with room for the mark, the number and the bar to
   breathe — which is what makes the ranking readable at a glance. */
/* THEMED SURFACES, not white-alpha (Braeden 8/11: "why is it all seem
   invisable"). rgba(255,255,255,.06) is a ghost on the black night page and
   completely invisible on the day page, which is a light lavender — a
   6%-white panel on #cbd5e6 renders as nothing. These use the app's own
   --surface/--border/--text vars, so the rows are solid cards that flip with
   the Night/Day switch like every other panel in the app. */
.lv-list { display:flex; flex-direction:column; gap:12px; }
.lv-row { display:grid; grid-template-columns:64px 1fr 120px 150px;
  gap:22px; align-items:center; padding:24px 26px; border-radius:18px; min-height:104px;
  position:relative; overflow:hidden;
  background:var(--surface); border:1px solid var(--border); color:var(--text);
  box-shadow:0 2px 10px rgba(0,0,0,.16); }
/* each channel's brand colour as a solid left edge */
.lv-row::before { content:''; position:absolute; left:0; top:0; bottom:0; width:5px;
  background:var(--brand, var(--border)); }
.lv-chip { width:56px; height:56px; border-radius:16px; display:grid; place-items:center;
  box-shadow:0 4px 14px rgba(0,0,0,.30); }
.lv-chip .lv-logo { width:30px; height:30px; }
.lv-row__name { font-weight:800; font-size:21px; letter-spacing:-.02em; line-height:1.2;
  color:var(--text); }
.lv-row__name small { display:block; font-weight:500; font-size:13px; margin-top:5px;
  letter-spacing:0; color:var(--text-muted); }
.lv-row__n { text-align:right; font-weight:800; font-size:34px; letter-spacing:-.035em;
  line-height:1; color:var(--text); }
.lv-row__n small { display:block; font-weight:600; font-size:11px; color:var(--text-muted);
  letter-spacing:.08em; text-transform:uppercase; margin-top:6px; }
/* the link to that channel's own management console */
.lv-row__go { justify-self:end; display:inline-block; padding:11px 18px; border-radius:11px;
  font-size:14px; font-weight:700; text-decoration:none; white-space:nowrap;
  color:#fff; background:var(--brand, #555); border:0; cursor:pointer;
  box-shadow:0 3px 10px rgba(0,0,0,.22);
  transition:transform .18s cubic-bezier(.16,1,.3,1), filter .18s; }
/* NO transform on hover. Moving an element while the pointer is on it shifts it
   out from under the cursor, hover un-triggers, it snaps back, hover re-triggers
   — that loop is the "jiggle". Colour and shadow only; nothing that changes
   layout or position. */
.lv-row__go:hover { filter:brightness(1.10); box-shadow:0 5px 16px rgba(0,0,0,.30); }
.lv-row__go.as-btn { font-family:inherit; }
/* Reads as a slot waiting to be filled, not as another live channel. */
.lv-addmore { display:block; width:100%; margin-top:12px; padding:20px; border-radius:18px;
  font:inherit; font-size:16px; font-weight:800; letter-spacing:-.01em; cursor:pointer;
  color:var(--text-muted); background:transparent;
  border:2px dashed var(--border);
  transition:color .18s, border-color .18s, background .18s; }
.lv-addmore:hover { color:var(--text); border-color:var(--text-muted);
  background:var(--surface); }

/* ── add-a-channel sheet ────────────────────────────────────────────────── */
.lv-modal { position:fixed; inset:0; z-index:120; display:grid; place-items:center;
  padding:24px; background:rgba(0,0,0,.55); backdrop-filter:blur(3px);
  animation:lvFade .16s ease both; }
@keyframes lvFade { from{opacity:0} to{opacity:1} }
.lv-sheet { width:min(620px,100%); max-height:min(760px,88vh); display:flex; flex-direction:column;
  background:var(--surface); color:var(--text); border:1px solid var(--border);
  border-radius:22px; box-shadow:0 30px 80px rgba(0,0,0,.5); padding:26px 26px 20px;
  animation:lvRise .22s cubic-bezier(.16,1,.3,1) both; }
@keyframes lvRise { from{transform:translateY(14px);opacity:0} to{transform:none;opacity:1} }
.lv-sheet__top { display:flex; justify-content:space-between; align-items:flex-start; gap:16px; }
.lv-sheet h3 { margin:0; font-size:24px; font-weight:800; letter-spacing:-.02em; }
.lv-sheet p { margin:6px 0 0; font-size:14px; color:var(--text-muted); line-height:1.5; }
.lv-x { border:0; background:transparent; color:var(--text-muted); font-size:30px; line-height:1;
  cursor:pointer; padding:0 4px; }
.lv-x:hover { color:var(--text); }
.lv-search { margin-top:18px; width:100%; font:inherit; font-size:15px; padding:14px 16px;
  border-radius:12px; color:var(--text); background:var(--input-bg,var(--surface));
  border:1px solid var(--border); }
.lv-search:focus { outline:none; border-color:var(--accent); }
.lv-choices { margin-top:14px; overflow-y:auto; display:flex; flex-direction:column; gap:8px;
  padding-right:2px; }
.lv-choice { display:grid; grid-template-columns:48px 1fr auto; gap:16px; align-items:center;
  padding:14px 16px; border-radius:14px; text-decoration:none; text-align:left;
  color:var(--text); background:transparent; border:1px solid var(--border);
  font:inherit; cursor:pointer; position:relative; overflow:hidden;
  transition:border-color .18s, background .18s; }
.lv-choice::before { content:''; position:absolute; left:0; top:0; bottom:0; width:4px;
  background:var(--brand, var(--border)); }
/* colour only — see the note on .lv-row__go:hover */
.lv-choice:hover { border-color:var(--brand, var(--text-muted));
  background:color-mix(in srgb, var(--brand, #888) 7%, transparent); }
.lv-choice .lv-chip { width:44px; height:44px; border-radius:13px; }
.lv-choice .lv-chip .lv-logo { width:24px; height:24px; }
.lv-choice__name { font-weight:800; font-size:17px; letter-spacing:-.01em; }
.lv-choice__name small { display:block; font-weight:500; font-size:12.5px; margin-top:3px;
  color:var(--text-muted); }
.lv-choice__go { font-size:13px; font-weight:700; color:var(--text-muted); white-space:nowrap; }
.lv-choice:hover .lv-choice__go { color:var(--text); }
/* Aria's line about the desktop app, pinned under the choices */
.lv-appnudge { display:grid; grid-template-columns:36px 1fr auto; gap:14px; align-items:center;
  margin-top:16px; padding:14px 16px; border-radius:14px; text-decoration:none;
  background:color-mix(in srgb, var(--accent) 12%, transparent);
  border:1px solid color-mix(in srgb, var(--accent) 40%, transparent);
  color:var(--text); }
.lv-appnudge:hover { background:color-mix(in srgb, var(--accent) 18%, transparent); }
.lv-appnudge__ic { width:36px; height:36px; border-radius:11px; display:grid; place-items:center;
  background:var(--accent); color:#1b1400; font-weight:800; font-size:17px; }
.lv-appnudge__tx { font-size:13px; line-height:1.5; color:var(--text-muted); }
.lv-appnudge__tx b { display:block; font-size:14.5px; color:var(--text); margin-bottom:2px; }
.lv-appnudge__go { font-size:13px; font-weight:800; color:var(--accent); white-space:nowrap; }

.lv-chip--ask { display:grid; place-items:center; background:var(--border); color:var(--text);
  font-weight:800; font-size:20px; }
.lv-choice--ask { border-style:dashed; }
@media (max-width:560px){
  .lv-sheet { padding:20px 18px 16px; border-radius:18px; }
  .lv-choice { grid-template-columns:40px 1fr; }
  .lv-choice__go { display:none; }
}
.lv-nometrics { margin-top:18px; padding:16px 20px; border-radius:14px; font-size:13.5px;
  line-height:1.55; color:var(--text-muted); background:var(--surface);
  border:1px dashed var(--border); max-width:640px; }
.lv-sectionhead { color:var(--text); }
.lv-row__n { text-align:right; font-weight:800; font-size:34px; letter-spacing:-.035em; line-height:1; }
.lv-row__n small { display:block; font-weight:600; font-size:11px; opacity:.55;
  letter-spacing:.08em; text-transform:uppercase; margin-top:6px; }
.lv-row__go { text-align:right; font-size:14px; font-weight:700; opacity:.62; white-space:nowrap; }
a.lv-row:hover .lv-row__go { opacity:1; }
.lv-row__connect { border:0; background:none; padding:0; cursor:pointer; color:inherit;
  font:inherit; font-size:14px; font-weight:700; text-align:right; white-space:nowrap; opacity:.85; }
@media (max-width:820px){
  .lv-row { grid-template-columns:56px 1fr 92px; gap:16px; padding:20px; min-height:0; }
  .lv-bar, .lv-row__go { display:none; }
}
.lv-card { border-radius:14px; padding:16px 20px 18px; color:#fff; text-decoration:none;
  display:block; box-shadow:0 4px 12px rgba(16,32,64,.18);
  transition:transform .28s cubic-bezier(.16,1,.3,1), box-shadow .28s cubic-bezier(.16,1,.3,1); }
a.lv-card:hover { transform:translateY(-2px); box-shadow:0 10px 26px rgba(16,32,64,.26); }
.lv-card__head { display:flex; align-items:center; gap:9px; }
/* the real logo, sized like a brand mark — no coloured square behind it */
.lv-logo { width:22px; height:22px; flex:none; display:block; }
.lv-logo svg { width:100%; height:100%; display:block; }
.lv-card__label { font-weight:700; font-size:14px; letter-spacing:-.01em; }
/* Google's card is white, so its border keeps it from vanishing on light ground */
.lv-card[style*="rgb(255, 255, 255)"] { box-shadow:0 4px 12px rgba(16,32,64,.16), inset 0 0 0 1px #e3e8f0; }
.lv-card__n { font-weight:800; letter-spacing:-.03em; font-size:clamp(32px,4vw,44px);
  line-height:1; margin-top:10px; }
.lv-connect, .lv-card__out { display:block; margin-top:10px; border:0; background:none; padding:0;
  cursor:pointer; font-weight:700; font-size:13px; color:#fff; opacity:.9; text-align:left; }
.lv-card__out { opacity:.72; font-size:12px; margin-top:6px; }

@media (max-width:640px) {
  .lv-grid, .lv-metrics { grid-template-columns:1fr; }
}
`;
